import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowLeft, CheckCircle, Warning, Eye, EyeSlash, FloppyDisk, Trash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";

/**
 * Shape of settings payload:
 * {
 *   default_stt_engine: "whisper" | "elevenlabs" | "google" | "azure",
 *   engines: [{ id, name, available, note }],
 *   keys: {
 *     elevenlabs_api_key: "xxxx••••yyyy",
 *     google_stt_api_key: "",
 *     azure_speech_key: "",
 *     azure_speech_region: "",
 *   }
 * }
 */

// Which env-var fields each engine needs + their human label.
const ENGINE_FIELDS = {
  whisper: [
    // OpenAI key is rendered in its own dedicated section above; we keep an
    // empty list here so the Flowscribe-WEngine block in the keys editor
    // disappears (no fields to configure beyond the shared OpenAI key).
  ],
  elevenlabs: [
    { key: "elevenlabs_api_key", label: "ELEVENLABS_API_KEY", type: "password",
      hint: "Get from elevenlabs.io → Profile → API keys" },
  ],
  google: [
    { key: "google_stt_api_key", label: "GOOGLE_STT_API_KEY", type: "password",
      hint: "Google Cloud Console → APIs & Services → Credentials. Speech-to-Text API must be enabled." },
    { key: "google_service_account_json", label: "GOOGLE_SERVICE_ACCOUNT_JSON", type: "textarea",
      hint: "Paste the FULL service-account JSON (Cloud Console → IAM → Service Accounts → Keys → JSON). Required: Flowscribe-GEngine ALWAYS uploads audio to the GCS bucket via this service account." },
    { key: "google_stt_gcs_bucket", label: "GOOGLE_STT_GCS_BUCKET", type: "text",
      placeholder: "flowscribe",
      hint: "Name of the private GCS bucket used as a temporary staging area for the audio. Each uploaded blob is auto-deleted after transcription finishes. Defaults to 'flowscribe' if left blank." },
  ],
  azure: [
    { key: "azure_speech_key", label: "AZURE_SPEECH_KEY", type: "password",
      hint: "Azure Portal → your Speech resource → Keys and Endpoint" },
    { key: "azure_speech_region", label: "AZURE_SPEECH_REGION", type: "text",
      hint: 'e.g. "eastus", "westeurope"' },
  ],
  // Pseudo-engine slot rendered alongside the STT engines so admins can paste
  // the Google Translate key in one obvious place.
  translation_google: [
    { key: "google_translate_api_key", label: "GOOGLE_TRANSLATE_API_KEY", type: "password",
      hint: "Google Cloud Console → APIs & Services → Credentials. Translate API must be enabled. Separate from the STT key." },
  ],
};

export default function SettingsPage() {
  const [settings, setSettings] = useState(null);
  const [draftKeys, setDraftKeys] = useState({}); // edited values (unsaved)
  const [showKey, setShowKey] = useState({});      // which fields reveal plaintext
  const [savingKeys, setSavingKeys] = useState(false);
  const [savingDefault, setSavingDefault] = useState(false);
  const [err, setErr] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/settings");
      setSettings(data);
      setDraftKeys({});
    } catch (e) {
      setErr(formatErr(e.response?.data?.detail) || e.message);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const onKeyChange = (field, value) => {
    setDraftKeys((prev) => ({ ...prev, [field]: value }));
  };

  const saveKeys = async () => {
    if (!Object.keys(draftKeys).length) return;
    setSavingKeys(true);
    try {
      const { data } = await api.patch("/settings", { stt_keys: draftKeys });
      setSettings(data);
      setDraftKeys({});
      toast.success("API keys saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSavingKeys(false);
    }
  };

  const clearKey = async (field) => {
    if (!window.confirm(`Clear ${field}?`)) return;
    try {
      const { data } = await api.patch("/settings", { stt_keys: { [field]: "" } });
      setSettings(data);
      setDraftKeys((prev) => { const n = { ...prev }; delete n[field]; return n; });
      toast.success(`${field} cleared`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const selectDefault = async (engineId) => {
    setSavingDefault(true);
    try {
      const { data } = await api.patch("/settings", { default_stt_engine: engineId });
      setSettings(data);
      toast.success(`Default engine set to ${engineId}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDefault(false);
    }
  };

  const selectTranslationEngine = async (engineId) => {
    setSavingDefault(true);
    try {
      const { data } = await api.patch("/settings", { translation_engine: engineId });
      setSettings(data);
      toast.success(`Translation engine set to ${engineId === "openai" ? "Flowscribe-GPEngine" : "Flowscribe-GTEngine"}`);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDefault(false);
    }
  };

  const toggleDiarization = async (next) => {
    setSavingDefault(true);
    try {
      const { data } = await api.patch("/settings", { feature_diarization_enabled: next });
      setSettings(data);
      toast.success(next ? "Speaker diarization enabled platform-wide" : "Speaker diarization hidden");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setSavingDefault(false);
    }
  };

  if (!settings && !err) {
    return <AppShell><div className="overline text-[--muted]">Loading…</div></AppShell>;
  }
  if (err) {
    return <AppShell><div className="text-red-600 font-mono text-sm">{err}</div></AppShell>;
  }

  const hasDraft = Object.keys(draftKeys).length > 0;

  return (
    <AppShell>
      <Link to="/dashboard" className="inline-flex items-center gap-2 overline text-[--muted] hover:text-[--accent] mb-6" data-testid="back-to-dashboard">
        <ArrowLeft weight="bold" size={14} /> Back to studio
      </Link>

      <div className="mb-10">
        <div className="overline mb-3 inline-flex items-center gap-2">
          <span className="px-1.5 py-0.5 bg-[--ink] text-white text-[10px] font-mono">PLATFORM</span>
          SETTINGS
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter">Speech engines</h1>
        <p className="mt-3 text-[--muted] max-w-2xl">
          These are <strong>platform-wide</strong> settings. Whatever you configure here is used by every
          Flowscribe user's transcriptions. Whisper runs out of the box; paste API keys below to enable
          ElevenLabs, Google, or Azure for everyone — or supply your own OpenAI key to take Whisper off
          the Emergent universal key.
        </p>
      </div>

      {/* Default engine selector */}
      <section className="mb-10">
        <div className="overline mb-3">DEFAULT ENGINE</div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-[--line] border border-[--line]">
          {settings.engines.map((e) => {
            const selected = e.id === settings.default_stt_engine;
            return (
              <button
                key={e.id}
                data-testid={`engine-card-${e.id}`}
                disabled={!e.available || savingDefault}
                onClick={() => e.available && selectDefault(e.id)}
                className={`text-left p-6 bg-white transition-colors ${
                  selected ? "outline outline-4 -outline-offset-4 outline-[--accent] bg-[--sky]" : ""
                } ${e.available ? "hover:bg-[--paper] cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="overline text-[--muted]">{e.id.toUpperCase()}</div>
                  {e.available ? (
                    <div className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle weight="fill" size={14} />
                      <span className="text-[10px] font-mono font-bold">READY</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 text-amber-600">
                      <Warning weight="fill" size={14} />
                      <span className="text-[10px] font-mono font-bold">KEY NEEDED</span>
                    </div>
                  )}
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight leading-tight">{e.name}</h3>
                {e.provider && (
                  <div className="font-mono text-[10px] text-[--muted] mt-1">{e.provider}</div>
                )}
                <p className="mt-2 text-xs text-[--muted] leading-relaxed">{e.note}</p>
                {selected && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-[--accent] text-white px-3 py-1.5 text-[10px] font-bold" data-testid="engine-selected-badge">
                    <CheckCircle weight="fill" size={12} /> ACTIVE
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </section>

      {/* Translation engine selector */}
      <section className="mb-10" data-testid="translation-engine-section">
        <div className="overline mb-3">TRANSLATION ENGINE</div>
        <div className="grid md:grid-cols-2 gap-px bg-[--line] border border-[--line]">
          {(settings.translation_engines || []).map((e) => {
            const selected = e.id === settings.translation_engine;
            return (
              <button
                key={e.id}
                data-testid={`translation-engine-card-${e.id}`}
                disabled={!e.available || savingDefault}
                onClick={() => e.available && selectTranslationEngine(e.id)}
                className={`text-left p-6 bg-white transition-colors ${
                  selected ? "outline outline-4 -outline-offset-4 outline-[--accent] bg-[--sky]" : ""
                } ${e.available ? "hover:bg-[--paper] cursor-pointer" : "opacity-50 cursor-not-allowed"}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="overline text-[--muted]">{e.id === "openai" ? "OPENAI LLM" : "GOOGLE TRANSLATE"}</div>
                  {e.available ? (
                    <div className="inline-flex items-center gap-1 text-emerald-600">
                      <CheckCircle weight="fill" size={14} />
                      <span className="text-[10px] font-mono font-bold">READY</span>
                    </div>
                  ) : (
                    <div className="inline-flex items-center gap-1 text-amber-600">
                      <Warning weight="fill" size={14} />
                      <span className="text-[10px] font-mono font-bold">KEY NEEDED</span>
                    </div>
                  )}
                </div>
                <h3 className="font-display text-lg font-bold tracking-tight leading-tight">{e.name}</h3>
                <p className="mt-2 text-xs text-[--muted] leading-relaxed">{e.note}</p>
                {selected && (
                  <div className="mt-4 inline-flex items-center gap-2 bg-[--accent] text-white px-3 py-1.5 text-[10px] font-bold">
                    <CheckCircle weight="fill" size={12} /> ACTIVE
                  </div>
                )}
              </button>
            );
          })}
        </div>
        <div className="hard-border bg-[--paper] p-4 mt-px text-xs text-[--ink]/75 leading-relaxed">
          Dialect conversions (e.g., Egyptian → Modern Standard Arabic) always use
          <strong> Flowscribe-GPEngine</strong> regardless of this setting — Google Translate has no dialect awareness.
        </div>
      </section>

      {/* Feature flags */}
      <section className="mb-10" data-testid="feature-flags-section">
        <div className="overline mb-3">FEATURES</div>
        <div className="hard-border bg-white divide-y divide-[--line]">
          <FeatureToggle
            id="diarization"
            label="Speaker diarization"
            desc="Label each segment with a speaker (Speaker 1, Speaker 2, …) on the project page. Hidden from all users until enabled here."
            value={!!settings.feature_diarization_enabled}
            onChange={toggleDiarization}
            disabled={savingDefault}
          />
        </div>
      </section>

      {/* Email / invites */}
      <section className="mb-10" data-testid="email-section">
        <div className="overline mb-3">INVITE EMAILS (RESEND)</div>
        <EmailConfigPanel
          settings={settings}
          onSaved={(data) => setSettings(data)}
        />
      </section>

      {/* Shared OpenAI key (Flowscribe-WEngine + Flowscribe-GPEngine) */}
      <section className="mb-10" data-testid="openai-key-section">
        <div className="overline mb-3">OPENAI KEY (FLOWSCRIBE-WENGINE + FLOWSCRIBE-GPENGINE)</div>
        <OpenAIKeyPanel
          savedValue={settings.keys?.openai_api_key || ""}
          draftValue={draftKeys.openai_api_key}
          onDraftChange={(v) => onKeyChange("openai_api_key", v)}
          onSave={async () => {
            if (draftKeys.openai_api_key === undefined) return;
            setSavingKeys(true);
            try {
              const { data } = await api.patch("/settings", {
                stt_keys: { openai_api_key: draftKeys.openai_api_key },
              });
              setSettings(data);
              setDraftKeys((p) => { const n = { ...p }; delete n.openai_api_key; return n; });
              toast.success(draftKeys.openai_api_key
                ? "OpenAI key saved — billed directly to your account"
                : "Cleared — falling back to Emergent Universal Key");
            } catch (e) {
              toast.error(formatErr(e.response?.data?.detail) || e.message);
            } finally {
              setSavingKeys(false);
            }
          }}
          onClear={() => clearKey("openai_api_key")}
          busy={savingKeys}
        />
      </section>

      {/* Keys editor */}
      <section className="mb-10">
        <div className="overline mb-3">API KEYS</div>
        <div className="hard-border bg-white">
          {Object.entries(ENGINE_FIELDS).map(([eng, fields]) => {
            if (!fields.length) return null;
            const engMeta = settings.engines.find((e) => e.id === eng)
              || (settings.translation_engines || []).find((e) => `translation_${e.id}` === eng)
              || { name: eng.replace(/_/g, " ").toUpperCase(), note: "", available: false };
            return (
              <div key={eng} className="p-6 border-b border-[--line] last:border-b-0" data-testid={`keys-block-${eng}`}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <div className="font-display text-xl font-bold tracking-tight">{engMeta.name}</div>
                    <div className="font-mono text-xs text-[--muted] mt-1">{engMeta.note}</div>
                  </div>
                  {engMeta.available && (
                    <div className="text-emerald-600 inline-flex items-center gap-1">
                      <CheckCircle weight="fill" size={14} />
                      <span className="font-mono text-[10px] font-bold">READY</span>
                    </div>
                  )}
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {fields.map((f) => {
                    const saved = settings.keys?.[f.key] || "";
                    const draftVal = draftKeys[f.key];
                    const displayVal = draftVal !== undefined ? draftVal : saved;
                    const isSecret = f.type === "password";
                    const isTextarea = f.type === "textarea";
                    const revealed = !!showKey[f.key];
                    return (
                      <div key={f.key}>
                        <label className="overline block mb-2">{f.label}</label>
                        <div className={`flex ${isTextarea ? "flex-col" : "items-stretch"} border border-[--ink]`}>
                          {isTextarea ? (
                            <textarea
                              value={displayVal}
                              onChange={(e) => onKeyChange(f.key, e.target.value)}
                              placeholder={saved ? "" : '{"type":"service_account", … }'}
                              data-testid={`key-input-${f.key}`}
                              rows={6}
                              className="flex-1 px-3 py-2.5 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-[--accent] resize-y min-h-[140px]"
                              spellCheck={false}
                            />
                          ) : (
                            <input
                              type={isSecret && !revealed ? "password" : "text"}
                              value={displayVal}
                              onChange={(e) => onKeyChange(f.key, e.target.value)}
                              placeholder={f.placeholder || (saved ? "" : "paste your key here")}
                              data-testid={`key-input-${f.key}`}
                              className="flex-1 px-3 py-2.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[--accent]"
                              autoComplete="new-password"
                              spellCheck={false}
                            />
                          )}
                          {isSecret && (
                            <button
                              type="button"
                              onClick={() => setShowKey((p) => ({ ...p, [f.key]: !p[f.key] }))}
                              className="px-3 border-l border-[--ink] hover:bg-[--sky] transition-colors"
                              aria-label="Toggle visibility"
                              data-testid={`key-toggle-${f.key}`}
                            >
                              {revealed ? <EyeSlash weight="bold" size={14} /> : <Eye weight="bold" size={14} />}
                            </button>
                          )}
                          {saved && !isTextarea && (
                            <button
                              type="button"
                              onClick={() => clearKey(f.key)}
                              className="px-3 border-l border-[--ink] hover:bg-[--accent] hover:text-white transition-colors"
                              aria-label="Clear"
                              data-testid={`key-clear-${f.key}`}
                            >
                              <Trash weight="bold" size={14} />
                            </button>
                          )}
                        </div>
                        <div className="mt-1.5 text-[11px] text-[--muted]">{f.hint}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          <div className="flex items-center justify-end gap-3 px-6 py-4 bg-[--paper] border-t border-[--line]">
            <div className="text-xs text-[--muted] mr-auto font-mono">
              {hasDraft ? `${Object.keys(draftKeys).length} unsaved change${Object.keys(draftKeys).length === 1 ? "" : "s"}` : "All changes saved"}
            </div>
            <button
              type="button"
              onClick={saveKeys}
              disabled={!hasDraft || savingKeys}
              data-testid="save-keys-btn"
              className="bg-[--accent] text-white px-5 py-2.5 text-sm font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
            >
              <FloppyDisk weight="bold" size={16} />
              {savingKeys ? "Saving…" : "Save keys"}
            </button>
          </div>
        </div>
      </section>

      <div className="hard-border bg-[--sky] p-5 text-xs text-[--ink]/75 leading-relaxed">
        These keys are stored on the platform-admin account and shared across all users. Every transcription
        — yours or any editor's — runs through this single configuration. Whisper falls back to the
        platform-provided Emergent Universal Key when no OpenAI key is set above.
      </div>
    </AppShell>
  );
}

function EmailConfigPanel({ settings, onSaved }) {
  const isAdmin = settings.is_admin;
  const email = settings.email || {};
  const [resendKey, setResendKey] = useState("");
  const [senderEmail, setSenderEmail] = useState(email.sender_email || "");
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => { setSenderEmail(email.sender_email || ""); }, [email.sender_email]);

  if (!isAdmin) return null;

  const dirty = resendKey !== "" || senderEmail !== (email.sender_email || "");

  const onSave = async () => {
    setBusy(true);
    try {
      const body = { sender_email: senderEmail };
      if (resendKey !== "") body.resend_api_key = resendKey;
      const { data } = await api.patch("/settings", body);
      onSaved(data);
      setResendKey("");
      toast.success("Email settings saved");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const onClear = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch("/settings", { resend_api_key: "" });
      onSaved(data);
      setResendKey("");
      toast.success("Resend key cleared — invite emails are now disabled");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="hard-border bg-white p-6" data-testid="email-config-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="font-display text-xl font-bold tracking-tight">Resend (transactional email)</div>
          <p className="text-xs text-[--muted] mt-1 leading-relaxed max-w-2xl">
            When an admin creates a user, Flowscribe will email them their temporary password and a
            login link. Add your Resend API key below and (optionally) a verified sender address.
          </p>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold border ${
            email.resend_configured ? "border-emerald-500 bg-emerald-50 text-emerald-700" : "border-[--line] bg-[--paper] text-[--muted]"
          }`}
          data-testid="email-resend-status"
        >
          {email.resend_configured ? "ACTIVE" : "NOT CONFIGURED"}
        </span>
      </div>

      <label className="text-xs font-bold uppercase tracking-widest text-[--muted]">RESEND_API_KEY</label>
      <div className="mt-2 flex items-stretch border border-[--ink]">
        <input
          type={revealed ? "text" : "password"}
          value={resendKey}
          onChange={(e) => setResendKey(e.target.value)}
          placeholder={email.resend_configured ? "•••••••• (leave blank to keep current)" : "re_..."}
          data-testid="resend-key-input"
          className="flex-1 px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[--accent]"
          autoComplete="new-password"
          spellCheck={false}
        />
        <button type="button" onClick={() => setRevealed((r) => !r)} className="px-3 border-l border-[--ink] hover:bg-[--sky] transition-colors">
          {revealed ? "HIDE" : "SHOW"}
        </button>
        {email.resend_configured && (
          <button
            type="button"
            onClick={onClear}
            disabled={busy}
            className="px-3 border-l border-[--ink] hover:bg-[--accent] hover:text-white transition-colors text-xs font-bold"
            data-testid="resend-key-clear"
          >
            CLEAR
          </button>
        )}
      </div>
      <p className="text-[11px] text-[--muted] font-mono mt-1">Get a key at <span className="font-mono">resend.com → API keys</span>.</p>

      <label className="text-xs font-bold uppercase tracking-widest text-[--muted] mt-5 block">FROM ADDRESS</label>
      <input
        type="email"
        value={senderEmail}
        onChange={(e) => setSenderEmail(e.target.value)}
        placeholder="onboarding@resend.dev"
        data-testid="sender-email-input"
        className="mt-2 w-full border border-[--ink] px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-[--accent]"
      />
      <p className="text-[11px] text-[--muted] font-mono mt-1">Must be a Resend-verified domain. Use <code>onboarding@resend.dev</code> for quick tests.</p>

      <div className="flex justify-end mt-4">
        <button
          type="button"
          onClick={onSave}
          disabled={!dirty || busy}
          data-testid="email-save-btn"
          className="bg-[--accent] text-white px-4 py-2 text-xs font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-40"
        >
          {busy ? "Saving…" : "Save email settings"}
        </button>
      </div>
    </div>
  );
}

function OpenAIKeyPanel({ savedValue, draftValue, onDraftChange, onSave, onClear, busy }) {
  const [revealed, setRevealed] = useState(false);
  const hasSaved = !!savedValue;
  const displayVal = draftValue !== undefined ? draftValue : (hasSaved ? savedValue : "");
  const isDirty = draftValue !== undefined && draftValue !== savedValue;
  const usingOwn = hasSaved && !isDirty;

  return (
    <div className="hard-border bg-white p-6" data-testid="openai-key-panel">
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="font-display text-xl font-bold tracking-tight">Your OpenAI API key</div>
          <p className="text-xs text-[--muted] mt-1 leading-relaxed max-w-2xl">
            Powers transcription (<strong>Flowscribe-WEngine</strong>) and translation / dialect
            rewrites (<strong>Flowscribe-GPEngine</strong>). Paste your own <code className="font-mono text-[--ink]">sk-…</code> key
            here to bill OpenAI directly. <strong>Leave it blank</strong> to use Flowscribe's bundled
            Emergent Universal Key — no setup required.
          </p>
        </div>
        <span
          className={`shrink-0 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-mono font-bold border ${
            usingOwn
              ? "border-emerald-500 bg-emerald-50 text-emerald-700"
              : "border-[--line] bg-[--paper] text-[--muted]"
          }`}
          data-testid="openai-key-status"
        >
          {usingOwn ? "USING YOUR KEY" : "USING EMERGENT DEFAULT"}
        </span>
      </div>

      <div className="flex items-stretch border border-[--ink]">
        <input
          type={revealed ? "text" : "password"}
          value={displayVal}
          onChange={(e) => onDraftChange(e.target.value)}
          placeholder="sk-…  (leave blank to use the Emergent Universal Key)"
          data-testid="openai-key-input"
          className="flex-1 px-4 py-3 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[--accent]"
          autoComplete="new-password"
          spellCheck={false}
        />
        <button
          type="button"
          onClick={() => setRevealed((r) => !r)}
          className="px-3 border-l border-[--ink] hover:bg-[--sky] transition-colors"
          aria-label="Toggle visibility"
          data-testid="openai-key-toggle"
        >
          {revealed ? <EyeSlash weight="bold" size={14} /> : <Eye weight="bold" size={14} />}
        </button>
        {hasSaved && (
          <button
            type="button"
            onClick={onClear}
            className="px-3 border-l border-[--ink] hover:bg-[--accent] hover:text-white transition-colors"
            aria-label="Clear"
            data-testid="openai-key-clear"
            title="Clear (revert to Emergent default)"
          >
            <Trash weight="bold" size={14} />
          </button>
        )}
      </div>

      <div className="flex items-center justify-between mt-3">
        <div className="text-[11px] text-[--muted]">
          Get a key at <span className="font-mono">platform.openai.com → API keys</span>.
        </div>
        <button
          type="button"
          onClick={onSave}
          disabled={!isDirty || busy}
          data-testid="openai-key-save"
          className="bg-[--accent] text-white px-4 py-2 text-xs font-bold hover:bg-[--accent-hi] transition-colors disabled:opacity-40 inline-flex items-center gap-2"
        >
          <FloppyDisk weight="bold" size={14} />
          {busy ? "Saving…" : "Save key"}
        </button>
      </div>
    </div>
  );
}

function FeatureToggle({ id, label, desc, value, onChange, disabled }) {
  return (
    <div className="flex items-start justify-between gap-6 p-6" data-testid={`feature-toggle-${id}`}>
      <div className="min-w-0">
        <div className="font-display text-xl font-bold tracking-tight">{label}</div>
        <div className="font-mono text-xs text-[--muted] mt-1 leading-relaxed">{desc}</div>
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={value}
        disabled={disabled}
        onClick={() => onChange(!value)}
        data-testid={`feature-toggle-${id}-switch`}
        className={`shrink-0 inline-flex h-7 w-12 items-center border border-[--ink] transition-colors disabled:opacity-40 ${
          value ? "bg-[--accent]" : "bg-white"
        }`}
      >
        <span
          className={`inline-block h-5 w-5 bg-[--ink] transition-transform ${
            value ? "translate-x-6" : "translate-x-1"
          }`}
        />
      </button>
    </div>
  );
}
